import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// --- Define the specific orderby type ---
export type OrderByOptions = "popularity" | "date" | "price-asc" | "price-desc";

// Define types for our state
export interface FilterState {
  category: number[];
  tag: number[];
  brand: string[]; // Changed from number[] to string[] to use slugs
  minPrice: number | null;
  maxPrice: number | null;
  search: string;
  orderby: OrderByOptions;
  onSale: boolean;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  perPage: number; // Add perPage
}

export interface UIState {
  activeView: "grid" | "list";
}

export interface ProductState {
  filters: FilterState;
  pagination: PaginationState;
  ui: UIState;
}

// Define types for our action payloads
interface CategoryActionPayload {
  action?: "clear";
  replace?: number[];
}

interface PriceRangePayload {
  minPrice: number | null;
  maxPrice: number | null;
}

const initialState: ProductState = {
  // Current filter state
  filters: {
    category: [],
    tag: [],
    brand: [], // Now stores string slugs
    minPrice: null,
    maxPrice: null,
    search: "",
    orderby: "date",
    onSale: false,
  },
  // Active pagination
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    perPage: 20, // Default to 20 products per page
  },
  // UI state
  ui: {
    activeView: "grid", // 'grid' or 'list'
  },
};

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      // Reset to page 1 when filters change
      state.pagination.currentPage = 1;
    },
    setCategory: (
      state,
      action: PayloadAction<number | CategoryActionPayload>
    ) => {
      // Check if this is a clear action
      if (
        typeof action.payload === "object" &&
        action.payload.action === "clear"
      ) {
        // Clear all category filters
        state.filters.category = [];
      }
      // Check if this is a replace action with whole array
      else if (typeof action.payload === "object" && action.payload.replace) {
        // Replace the entire category array
        state.filters.category = action.payload.replace;
      } else {
        const category = action.payload as number;

        // If category is already in the array, remove it (toggle behavior)
        // This allows toggling both parent and child categories independently
        if (state.filters.category.includes(category)) {
          state.filters.category = state.filters.category.filter(
            (cat) => cat !== category
          );
        } else {
          // Otherwise add it to the array of selected categories
          // This maintains multiple selected categories for hierarchical navigation
          state.filters.category = [...state.filters.category, category];
        }
      }

      // Reset to page 1 when category changes
      state.pagination.currentPage = 1;
    },
    setTag: (state, action: PayloadAction<number>) => {
      const tag = action.payload;

      // If tag is already in the array, remove it (toggle behavior)
      if (state.filters.tag.includes(tag)) {
        state.filters.tag = state.filters.tag.filter((t) => t !== tag);
      } else {
        // Otherwise add it
        state.filters.tag = [...state.filters.tag, tag];
      }

      // Reset to page 1 when tag changes
      state.pagination.currentPage = 1;
    },
    // Update setBrand reducer to work with slugs
    setBrand: (state, action: PayloadAction<string>) => {
      const brandSlug = action.payload;
      // Toggle behavior: add if not present, remove if present
      if (state.filters.brand.includes(brandSlug)) {
        state.filters.brand = state.filters.brand.filter(
          (slug) => slug !== brandSlug
        );
      } else {
        state.filters.brand = [...state.filters.brand, brandSlug];
      }
      // Reset to page 1 when brand changes
      state.pagination.currentPage = 1;
    },
    setPriceRange: (state, action: PayloadAction<PriceRangePayload>) => {
      const { minPrice, maxPrice } = action.payload;
      state.filters.minPrice = minPrice;
      state.filters.maxPrice = maxPrice;
      state.pagination.currentPage = 1;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.currentPage = 1; // Reset page on new search
    },
    setSorting: (state, action: PayloadAction<OrderByOptions>) => {
      state.filters.orderby = action.payload;

      // Reset to page 1 when sort changes
      state.pagination.currentPage = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.perPage = action.payload;
      state.pagination.currentPage = 1; // Reset to page 1 when perPage changes
    },
    setActiveView: (state, action: PayloadAction<"grid" | "list">) => {
      state.ui.activeView = action.payload;
    },
    setOnSale: (state, action: PayloadAction<boolean>) => {
      state.filters.onSale = action.payload;
      state.pagination.currentPage = 1;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
  },
});

export const {
  setFilters,
  setCategory,
  setTag,
  setBrand,
  setPriceRange,
  setSearch,
  setSorting,
  setPagination,
  setPage,
  setPerPage, // Export the new action
  setActiveView,
  setOnSale,
  resetFilters,
} = productSlice.actions;

export default productSlice.reducer;
